/**
 * Jam — bug reporting / screen capture for developers
 *
 * A single WAR entry exposes `assets/*`, `injected-scripts/*`, `icom.html` and
 * `iui.html` to every http(s) origin (and `file:///*`) with no `use_dynamic_url` — so
 * its icon set and injected UI scripts are all fetchable from any page at a fixed URL.
 *
 * The bundled icon is the cleanest signal: an image loads through `probeImage` with no
 * CORS grant, so it survives a restrictive page CSP. The build-numbered scripts in
 * `injected-scripts/` (e.g. 448.js) rotate per release, so we corroborate with the
 * vendor-named ones instead.
 *
 * Manifest verified against version 5.71.1.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'iohjgamcilhbgmhbnllfolmkmmekfmci';

export const jam: ExtensionDefinition = {
  id: ID,
  name: 'Jam',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 200_000,
  notes:
    'Exposes its assets and injected-scripts to every http(s) origin with no dynamic URL. ' +
    'The icon loads without a CORS grant; the vendor-named scripts corroborate.',
  signals: [
    {
      id: 'war-icon-128',
      kind: 'web-accessible-resource',
      description:
        'Load assets/develop-128x128.png via <img> — a bundled icon exposed to every ' +
        'http(s) origin; needs no CORS grant, so it works even under a strict page CSP.',
      weight: 1,
      run: () => probeImage(ID, 'assets/develop-128x128.png'),
    },
    {
      id: 'war-content-jam-ui-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch injected-scripts/content-jam-ui.js — a vendor-named injected script, ' +
        'stable across releases unlike the build-numbered ones in the same folder.',
      weight: 0.5,
      run: () => probeFetch(ID, 'injected-scripts/content-jam-ui.js'),
    },
    {
      id: 'war-icom-js',
      kind: 'web-accessible-resource',
      description:
        'Fetch injected-scripts/icom.js — a second vendor-named script, corroborating ' +
        'the identification.',
      weight: 0.5,
      run: () => probeFetch(ID, 'injected-scripts/icom.js'),
    },
  ],
};
